import React from 'react';
import gql from 'graphql-tag';
import { compose, graphql } from 'react-apollo';
import { SubmissionError } from 'redux-form/immutable';
import { PLS_FEATURE_ENABLED } from 'utils/remoteConfigKeys';
import withRemoteConfigEntry from 'utils/withRemoteConfigEntry';

function SignUpContainer({
  signUpViewComponent: SignUpViewComponent,
  location,
  doNewUserSignUp,
  validateFields,
  signUpData
}) {
  if (!signUpData.shouldDisplaySignUp && signUpData.loading) {
    return null;
  }

  return (
    <SignUpViewComponent
      onFormSubmit={doNewUserSignUp}
      validateFields={validateFields}
      shouldDisplaySignUp={signUpData.shouldDisplaySignUp}
    />
  );
}

const VALIDATION_MUTATION = gql`
  mutation MyValidateSignUp($input: ValidateNewUserSignUpInput!) {
    validateNewUserSignUp(input: $input) {
      emailErrorMessage
      displayNameErrorMessage
      passwordErrorMessage
    }
  }
`;

const withValidate = graphql(VALIDATION_MUTATION, {
  props: ({ ownProps, mutate }) => ({
    validateFields: input =>
      mutate({ variables: { input } })
        .then(({ data }) => {
          const result = data && data.validateNewUserSignUp;
          if (!result) {
            throw new Error('Unexpected response');
          } else {
            const errors = {};
            let success = true;
            for (let key in result) {
              if (
                result.hasOwnProperty(key) &&
                key.endsWith('ErrorMessage') &&
                result[key] &&
                result[key].length
              ) {
                let field = key.substring(0, key.length - 12);
                errors[field] = result[key].join(' ');
                success = false;
              }
            }
            if (!success) {
              return errors;
            }
          }
          return undefined;
        })
        .catch(error => {
          if (error.message) {
            console.error(error.message);
          } else {
            console.error(JSON.stringify(error));
          }
          return undefined;
        })
  })
});

const SIGN_UP_MUTATION = gql`
  mutation MySignUp($input: NewUserSignUpInput!) {
    newUserSignup(newUserSignUpInput: $input) {
      success
      firstNameErrorMessage
      lastNameErrorMessage
      emailErrorMessage
      displayNameErrorMessage
      dateOfBirthErrorMessage
      passwordErrorMessage
      confirmPasswordErrorMessage
      agreeToTOSErrorMessage
      existingRewardsNumberErrorMessage
      eventCodeErrorMessage
      pinErrorMessage
      genderErrorMessage
      addressErrorMessage
      cityErrorMessage
      countryErrorMessage
      stateErrorMessage
      zipCodeErrorMessage
      mobilePhoneErrorMessage
      allowReceiveRewardsViaTextErrorMessage
      genericError
    }
  }
`;

const withSignUp = graphql(SIGN_UP_MUTATION, {
  props: ({ ownProps, mutate }) => ({
    doNewUserSignUp: input =>
      mutate({ variables: { input } })
        .then(({ data }) => {
          const result = data && data.newUserSignup;
          if (!result) {
            throw new Error('Unexpected response');
          } else if (!result.success) {
            const errors = { _error: 'Signup Unsuccessful.' };
            for (let key in result) {
              if (
                result.hasOwnProperty(key) &&
                key.endsWith('ErrorMessage') &&
                result[key] &&
                result[key].length
              ) {
                let field = key.substring(0, key.length - 12);
                errors[field] = result[key].join(' ');
              }
            }
            if (result.genericError && result.genericError.length) {
              errors._error = result.genericError.join('. ');
            }
            throw new SubmissionError(errors);
          }
          return result;
        })
        .catch(error => {
          if (error instanceof SubmissionError) {
            throw error;
          } else {
            if (error.message) {
              console.error(error.message);
            } else {
              console.error(JSON.stringify(error));
            }
            throw new SubmissionError({
              _error:
                'An error occurred while processing your request, please try again. If the problem persists, please contact Support.'
            });
          }
        })
  })
});

const SHOULD_DISPLAY_SIGN_UP_QUERY = gql`
  query {
    shouldDisplaySignUp {
      enabled
      message
    }
  }
`;

const withShouldDisplaySignUp = graphql(SHOULD_DISPLAY_SIGN_UP_QUERY, {
  name: 'signUpData',
  options: {
    fetchPolicy: 'network-only'
  }
});

export default compose(
  withShouldDisplaySignUp,
  withValidate,
  withSignUp,
  withRemoteConfigEntry(PLS_FEATURE_ENABLED)
)(SignUpContainer);
